import { captureMessage } from '@/services/sentry.service';

/**
 * Self-healing for stale-deploy chunk failures.
 *
 * When a deploy removes the hashed chunk a cached page still references, the
 * dynamic import gets the SPA's index.html fallback and rejects (Safari:
 * "'text/html' is not a valid JavaScript MIME type"). A full page reload
 * fetches fresh HTML with current chunk URLs, so on failure we reload — at
 * most once, gated by a `chunkReload=1` query param rather than storage: the
 * param survives the reload by construction, including in Safari private mode
 * where sessionStorage is blocked (an in-memory fallback there would reset on
 * every reload and loop forever). The param is stripped once a chunk loads, so
 * a later mid-session deploy can self-heal again.
 */

const RELOAD_PARAM = 'chunkReload';
// A guard stamp younger than this marks the current page load as the one
// retry — refuse a second reload so a persistent failure can't loop. An older
// stamp is a leftover from a previous failure episode (only the payment-form
// loader clears the param on success; generic chunks that recovered via the
// global listener keep it in the URL), so a fresh failure gets one new reload.
const RELOAD_TTL_MS = 60 * 1000;

/**
 * Reload the page once to pick up fresh asset URLs. Returns true when the
 * reload was initiated, false when this page load already is the one retry
 * (the guard param carries a recent timestamp) and the caller should fall
 * back to its error UI.
 *
 * @param {Object} [context] - Extra Sentry context (e.g. `{ trigger, error }`).
 */
export function guardedChunkReload(context = {}) {
  const url = new URL(window.location.href);
  const stamp = Number(url.searchParams.get(RELOAD_PARAM));
  if (stamp && Date.now() - stamp < RELOAD_TTL_MS) {
    return false;
  }

  url.searchParams.set(RELOAD_PARAM, String(Date.now()));
  captureMessage('Chunk failed to load; reloading once for fresh assets', {
    level: 'warning',
    logToConsole: true,
    context: { tags: { source: 'async_chunk', action: 'reload' }, extra: context }
  });
  window.location.assign(url.toString());
  return true;
}

/** Remove the one-shot guard param after a successful chunk load. */
export function clearChunkReloadParam() {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(RELOAD_PARAM)) {
    return;
  }
  url.searchParams.delete(RELOAD_PARAM);
  window.history.replaceState(window.history.state, '', url.toString());
}

/**
 * Build `{ loader, onError }` options for `defineAsyncComponent`:
 * retry the import once (transient flake), then reload once via
 * guardedChunkReload, then `fail()` so the `errorComponent` renders.
 *
 * @param {() => Promise<*>} loader - The dynamic `import()` thunk.
 * @param {Object} [options]
 * @param {string} [options.name] - Chunk label for Sentry context.
 */
export function reloadingAsyncLoader(loader, { name = 'async component' } = {}) {
  return {
    loader: () =>
      loader().then((module) => {
        clearChunkReloadParam();
        return module;
      }),

    // Vue passes attempts starting at 1 on the first failure (retries + 1).
    onError(error, retry, fail, attempts) {
      if (attempts <= 1) {
        retry();
        return;
      }

      if (guardedChunkReload({ trigger: 'component', name, attempts, error: String(error) })) {
        return;
      }

      captureMessage('Chunk still failing after reload; giving up', {
        level: 'error',
        logToConsole: true,
        context: {
          tags: { source: 'async_chunk', action: 'fail' },
          extra: { trigger: 'component', name, attempts, error: String(error) }
        }
      });
      fail();
    }
  };
}
